import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, SmartphoneIcon, PenLineIcon, BrainIcon, MoonIcon } from 'lucide-react';
export function RealityCheckSection() {
  const painPoints = [{
    icon: SmartphoneIcon,
    text: 'Abres la laptop con la mejor intención del mundo y a los 5 minutos ya estás revisando Instagram sin saber por qué.'
  }, {
    icon: PenLineIcon,
    text: 'Empiezas a escribir un post y borras todo tres veces porque nada suena bien.'
  }, {
    icon: BrainIcon,
    text: 'Intentas concentrarte pero tu cabeza sigue en la conversación que tuviste hace una hora, en la factura que vence el viernes, en si los niños comieron bien hoy.'
  }, {
    icon: MoonIcon,
    text: 'Y cuando termina el día, esa voz interior aparece puntual como siempre: "Otro día que no avancé nada. Mañana sí arranco de verdad."'
  }];
  const checkboxItems = ['Abriste el teléfono y no recordabas para qué lo habías agarrado', 'Empezaste una tarea y a los 5 minutos estabas haciendo otra cosa', 'Sentiste que trabajaste todo el día pero al final no sabes qué lograste exactamente', 'Te costó tomar una decisión simple sobre tu negocio', 'Te fuiste a dormir con la sensación de que mañana sí de verdad', 'Sentiste culpa por no ser tan productiva como crees que deberías ser'];
  return <section className="w-full bg-white px-4 md:px-6 py-6 md:py-24" data-id="element-314">
      <div className="max-w-4xl mx-auto" data-id="element-315">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-5 md:mb-12" data-id="element-316">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl lg:text-5xl font-bold text-[#2D2D2D] mb-3 md:mb-6" data-id="element-317">
            Seamos honestas por un momento.
          </h2>

          <p className="text-base md:text-xl text-[#2D2D2D] leading-relaxed max-w-2xl mx-auto" data-id="element-318">
            Tienes el negocio. Tienes las ganas. Tienes el producto o el
            servicio. Pero hay algo que pasa todos los días que{' '}
            <span className="font-semibold" data-id="element-319">nadie habla</span> en los grupos de
            emprendedoras:
          </p>
        </motion.div>

        {/* Highlighted pain statement */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-[#E8734A] rounded-2xl p-5 md:p-10 text-center mb-5 md:mb-12" data-id="element-320">
          <p className="text-xl md:text-3xl font-['Playfair_Display'] font-bold text-white leading-snug" data-id="element-321">
            Cuando por fin se hace el silencio y te toca trabajar...
            <br data-id="element-322" />
            <span className="text-[#FFF8F0]" data-id="element-323">te quedas en blanco.</span>
          </p>
        </motion.div>

        {/* Pain point cards */}
        <div className="grid md:grid-cols-2 gap-2 md:gap-5 mb-5 md:mb-14" data-id="element-324">
          {painPoints.map((point, index) => {
          const Icon = point.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="bg-[#FFF8F0] rounded-xl p-4 md:p-6 flex gap-3 md:gap-4 items-start" data-id="element-325">
                <div className="flex-shrink-0 w-11 h-11 bg-[#F5E6DC] rounded-full flex items-center justify-center" data-id="element-326">
                  <Icon className="w-5 h-5 text-[#E8734A]" data-id="element-327" />
                </div>
                <p className="text-[#2D2D2D] leading-relaxed" data-id="element-328">{point.text}</p>
              </motion.div>;
        })}
        </div>

        {/* Te suena familiar? */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center mb-4 md:mb-10" data-id="element-329">
          <p className="text-xl md:text-3xl font-['Playfair_Display'] font-bold text-[#2D2D2D]" data-id="element-330">
            ¿Te suena familiar?
          </p>
          <p className="text-lg text-[#2D2D2D] mt-2 md:mt-3 opacity-75" data-id="element-331">
            Si marcaste aunque sea una de estas situaciones en los últimos 7
            días, sigue leyendo:
          </p>
        </motion.div>

        {/* Interactive checklist */}
        <div className="grid sm:grid-cols-2 gap-2 md:gap-4 mb-4 md:mb-10" data-id="element-332">
          {checkboxItems.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.08
        }} className="bg-white border-2 border-[#F5E6DC] rounded-xl p-3 md:p-5 flex items-start gap-3 hover:border-[#E8734A] transition-colors" data-id="element-333">
              <div className="flex-shrink-0 w-7 h-7 rounded-md bg-[#E8734A] flex items-center justify-center mt-0.5" data-id="element-334">
                <CheckIcon className="w-4 h-4 text-white" data-id="element-335" />
              </div>
              <p className="text-[#2D2D2D] leading-relaxed text-sm md:text-base" data-id="element-336">
                {item}
              </p>
            </motion.div>)}
        </div>

        {/* Bottom callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-[#2D2D2D] rounded-2xl p-5 md:p-8 text-center" data-id="element-337">
          <p className="text-xl md:text-2xl text-white leading-relaxed" data-id="element-338">
            Si marcaste 3 o más,{' '}
            <strong className="text-[#E8734A]" data-id="element-339">no te pasa nada malo</strong>.
          </p>
          <p className="text-lg text-white opacity-90 mt-2 md:mt-3" data-id="element-340">
            Tu cerebro está saturado. Y hay una diferencia enorme entre{' '}
            <span className="text-[#D4A853] font-semibold" data-id="element-341">estar saturada</span>{' '}
            y <span className="text-[#D4A853] font-semibold" data-id="element-342">ser incapaz</span>.
          </p>
        </motion.div>
      </div>
    </section>;
}